import React, { useState } from "react";
import { Box, Typography, TextField, Tabs, Tab } from "@mui/material";
import { BrandBatchSettings } from "../../../pages/playground/types";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type InstructionType =
  | "title_instructions"
  | "fabric_instructions"
  | "color_instructions"
  | "country_of_origin_instructions"
  | "details_instructions"
  | "description_instructions";

type InstructionConfig = {
  key: InstructionType;
  label: string;
  description: string;
};

const instructionConfigs: InstructionConfig[] = [
  {
    key: "title_instructions",
    label: "Title",
    description: "Instructions for title generation",
  },
  {
    key: "description_instructions",
    label: "Description",
    description: "Instructions for product description generation",
  },
  {
    key: "fabric_instructions",
    label: "Fabric",
    description: "Instructions for material description",
  },
  {
    key: "color_instructions",
    label: "Color",
    description: "Instructions for color",
  },
  {
    key: "country_of_origin_instructions",
    label: "Country of Origin",
    description: "Instructions for country of origin",
  },
  {
    key: "details_instructions",
    label: "Details",
    description: "Instructions for details",
  },
];

type InstructionsProps = {
  settings: BrandBatchSettings;
  onChange: (key: string, settings: BrandBatchSettings) => void;
};

const Instructions: React.FC<InstructionsProps> = ({ settings, onChange }) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInstructionChange = (key: InstructionType, value: string) => {
    onChange(key, {
      ...settings,
      [key]: value,
    });
  };

  const currentConfig = instructionConfigs[activeTab];
  const currentKey = currentConfig.key;
  const currentValue = settings?.[currentKey] || "";

  return (
    <CollapsibleFormElement title="Generation Instructions">
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {instructionConfigs.map((config) => (
          <Tab
            key={config.key}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {config.label}
              </Box>
            }
            sx={{ textTransform: "none" }}
          />
        ))}
      </Tabs>

      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">{currentConfig.label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {currentConfig.description}
          </Typography>
        </Box>

        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder={`Enter instructions for ${currentConfig.label.toLowerCase()}...`}
          value={currentValue}
          onChange={(e) => handleInstructionChange(currentKey, e.target.value)}
          variant="outlined"
          size="small"
        />
      </Box>
    </CollapsibleFormElement>
  );
};

export default Instructions;
